import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { GenericService } from 'src/app/services/generic.service';
import * as XLSX from 'xlsx';
import 'leaflet-draw';
declare const L: any;

@Component({
  selector: 'app-upload-bulk-asset-map',
  templateUrl: './upload-bulk-asset-map.component.html',
  styleUrls: ['./upload-bulk-asset-map.component.css']
})
export class UploadBulkAssetMapComponent implements OnInit, OnChanges {

  listingResponse: any[] = [];

  private map!: L.Map;
  private markers: any[] = [];
  private defaultCenter: any = [17.4065, 78.4772];
  private defaultZoom: any = 10;
  private drawControl: any;


  isFullscreen: boolean = false;
  isLoading: boolean = false;


  constructor(private toastr: ToastrService, private generic_service: GenericService) { }

  get checkedCount(): number {
    return this.listingResponse.filter(item => item.selected).length;
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 300);
  }

  toggleSelection(index: number) {
    const isCurrentlySelected = this.listingResponse[index].selected;

    // Deselect all
    this.listingResponse.forEach((item, i) => {
      if (item.selected) {
        item.selected = false;
        if (this.markers[i]) {
          if (item.multilatlng) {
            this.markers[i].eachLayer((layer: any) => {
              if (layer instanceof L.Polyline && !(layer instanceof L.Marker)) {
                layer.setStyle({ color: '#2196f3' });
              }
            });
          } else {
            if (this.markers[i].dragging) {
              this.markers[i].dragging.disable();
            }
            this.markers[i].setIcon(this.getMarkerIcon(item));
            this.markers[i].setZIndexOffset(0);
          }
        }
      }
    });

    if (!isCurrentlySelected) {
      // Select the clicked one
      this.listingResponse[index].selected = true;
      if (this.markers[index]) {
        if (this.listingResponse[index].multilatlng) {
          this.markers[index].eachLayer((layer: any) => {
            if (layer instanceof L.Polyline && !(layer instanceof L.Marker)) {
              layer.setStyle({ color: '#ff9800' });
            }
          });
          if (this.markers[index].bringToFront) {
            this.markers[index].bringToFront();
          }
        } else {
          if (this.markers[index].dragging) {
            this.markers[index].dragging.enable();
          }
          this.markers[index].setIcon(this.getMarkerIcon(this.listingResponse[index]));
          this.markers[index].setZIndexOffset(1000); // bring to front
        }
      }

      const item = this.listingResponse[index];
      if (item.multilatlng) {
        if (item.latlng && item.latlng.length > 0) {
          const latLngs = item.latlng.map((p: any) => [p.lat, p.lng]);
          this.map.fitBounds(latLngs, { padding: [50, 50], duration: 1.5 });
        } else {
          this.toastr.info('Click on the map to add points for the line.', 'Location Missing');
        }
      } else {
        if (this.map && item.lat && item.lng) {
          // Zoom in to the selected location
          this.map.flyTo([item.lat, item.lng], 15, {
            duration: 1.5
          });
        } else if (!item.lat || !item.lng) {
          this.toastr.info('Click on the map to mark the location for this asset.', 'Location Missing');
        }
      }
    } else {
      // If unselecting, zoom out to fit all markers
      if (this.map) {
        const latLngs: any[] = [];
        this.listingResponse.forEach(item => {
          if (item.multilatlng) {
            if (item.latlng && item.latlng.length > 0) {
              item.latlng.forEach((p: any) => latLngs.push([p.lat, p.lng]));
            }
          } else {
            if (item.lat && item.lng) {
              latLngs.push([item.lat, item.lng]);
            }
          }
        });
        if (latLngs.length > 0) {
          this.map.fitBounds(latLngs, { padding: [50, 50], duration: 1.5 });
        }
      }
    }
  }

  private getMarkerIcon(item: any): any {
    const isSelected = item.selected;
    const borderColor = isSelected ? '#ff9800' : '#2196f3'; // Orange if selected, else Blue
    const iconClass = 'bi-geo-alt-fill';
    const iconSize = isSelected ? 45 : 30; // Bigger if selected
    const markerHtml = `
      <div style="
        position: relative;
        width: ${iconSize}px;
        height: ${iconSize}px;
        background: ${borderColor};
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: ${isSelected ? 22 : 16}px;
        box-shadow: 0 4px 10px rgba(0,0,0,0.4);
        transition: all 0.3s ease;
      ">
        <i class="bi ${iconClass}"></i>
      </div>
    `;

    return L.divIcon({
      className: 'custom-leaflet-marker',
      html: markerHtml,
      iconAnchor: [iconSize / 2, iconSize / 2],
      popupAnchor: [0, -(iconSize / 2) - 8] // Adjusted slightly higher to avoid overlap
    });
  }

  private getAngle(p1: [number, number], p2: [number, number]): number {
    if (!this.map) return 0;
    const p1Point = this.map.latLngToLayerPoint(L.latLng(p1[0], p1[1]));
    const p2Point = this.map.latLngToLayerPoint(L.latLng(p2[0], p2[1]));
    const dy = p2Point.y - p1Point.y;
    const dx = p2Point.x - p1Point.x;
    return Math.atan2(dy, dx) * 180 / Math.PI;
  }

  private getArrowIcon(color: string, rotation: number): any {
    const markerHtml = `
      <div style="
        width: 24px;
        height: 24px;
        color: ${color};
        font-size: 24px;
        line-height: 1;
        transform: rotate(${rotation}deg);
        display: flex;
        align-items: center;
        justify-content: center;
        text-shadow: 0px 2px 4px rgba(0,0,0,0.5);
      ">
        <i class="bi bi-arrow-right-circle-fill" style="margin: 0; padding: 0;"></i>
      </div>
    `;

    return L.divIcon({
      className: 'custom-arrow-marker',
      html: markerHtml,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
  }

  addAssetClick() {
    document.getElementById('add-btn')?.click();
  }

  downloadSample() {
    // Generate an empty sheet with just the headers
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([], {
      header: ['objectInfoId', 'name', 'lat', 'lng', 'multilatlng', 'latlng']
    });
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sample_Locations');
    XLSX.writeFile(wb, 'Asset_Location_Sample.xlsx');
  }

  onExcelFileChange(event: any, fileInput: any) {
    const target: DataTransfer = <DataTransfer>(event.target);
    if (target.files.length !== 1) {
      console.error('Please upload a single file.');
      return;
    }

    const file = target.files[0];
    const reader: FileReader = new FileReader();

    reader.onload = (e: any) => {
      const binaryStr: string = e.target.result;
      const workbook: XLSX.WorkBook = XLSX.read(binaryStr, { type: 'binary' });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      console.log('Parsed Excel Data:', rawData);

      // Convert label-based keys to what the map needs
      const mappedData = rawData.map((row: any, index: number) => {
        const parsedLat = parseFloat(row['lat'] || row['Lat']);
        const parsedLng = parseFloat(row['lng'] || row['Lng']);

        let multilatlng = row['multilatlng'];
        if (typeof multilatlng === 'string') {
          multilatlng = multilatlng.toLowerCase() === 'true';
        } else {
          multilatlng = !!multilatlng;
        }

        let latlng = [];
        if (row['latlng']) {
          try {
            latlng = typeof row['latlng'] === 'string' ? JSON.parse(row['latlng']) : row['latlng'];
          } catch (e) {
            console.error("Error parsing latlng", e);
          }
        }

        return {
          ...row, // Keep original data
          objectInfoId: row['objectInfoId'] ? Number(row['objectInfoId']) : index + 1,
          lat: isNaN(parsedLat) ? null : parsedLat,
          lng: isNaN(parsedLng) ? null : parsedLng,
          name: row['name'] || row['Name'] || row['Location'] || row['givenName'] || `Asset ${index + 1}`,
          multilatlng: multilatlng,
          latlng: Array.isArray(latlng) ? latlng : [],
          selected: false
        };
      });

      this.listingResponse = mappedData;
      fileInput.value = ''; // Reset file input

      // Update the map
      this.initMap();
    };

    reader.readAsBinaryString(file);
  }

  refresh() {
    console.log('Clearing map data...');
    this.listingResponse = [];
    const fileInput = document.getElementById('add-btn') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.initMap();
  }

  submit() {
    const unmappedItems = this.listingResponse.filter(item =>
      (item.multilatlng ? (!item.latlng || item.latlng.length === 0) : (!item.lat || !item.lng))
    );
    if (unmappedItems.length > 0) {
      this.toastr.error('Please assign locations to all assets before submitting.');
      return;
    }

    const submitData = this.listingResponse.map(item => ({
      objectInfoId: item.objectInfoId !== undefined && item.objectInfoId !== null ? Number(item.objectInfoId) : 0,
      name: item.name || '',
      lat: item.multilatlng ? 0 : (item.lat !== undefined && item.lat !== null ? Number(item.lat) : 0),
      lng: item.multilatlng ? 0 : (item.lng !== undefined && item.lng !== null ? Number(item.lng) : 0),
      multiLatLng: !!item.multilatlng,
      area: (item.latlng || []).map((p: any) => ({
        lat: p.lat !== undefined && p.lat !== null ? String(p.lat) : '',
        lng: p.lng !== undefined && p.lng !== null ? String(p.lng) : ''
      }))
    }));
    console.log('submitData', submitData);

    this.isLoading = true;
    this.generic_service.updateBulkObjectLatLngRequest(submitData).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastr.success('Assets updated successfully');
        this.listingResponse = [];
        this.initMap();
      },
      error: (error) => {
        this.isLoading = false;
        this.toastr.error(error.error.errors[0].description);
        this.refresh();
      }
    });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.initMap();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['listingResponse'] && !changes['listingResponse'].firstChange) {
      this.initMap();
    }
  }

  initMap() {
    if (this.map) {
      this.map.remove();
    }

    this.map = L.map('map', {
      center: this.defaultCenter,
      zoom: this.defaultZoom
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    this.drawControl = new L.Control.Draw({
      draw: {
        polyline: {
          shapeOptions: {
            color: '#ff9800',
            weight: 5
          }
        },
        polygon: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false
      },
      edit: false
    });
    this.map.addControl(this.drawControl);

    this.map.on(L.Draw.Event.CREATED, (e: any) => {
      const type = e.layerType,
        layer = e.layer;

      if (type === 'polyline') {
        const selectedIndex = this.listingResponse.findIndex((item: any) => item.selected);
        if (selectedIndex !== -1) {
          const item = this.listingResponse[selectedIndex];
          if (item.multilatlng) {
            const latlngs = layer.getLatLngs();
            item.latlng = latlngs.map((l: any) => ({ lat: l.lat, lng: l.lng }));
            if (item.latlng.length > 0) {
              item.lat = item.latlng[0].lat;
              item.lng = item.latlng[0].lng;
            }

            if (this.markers[selectedIndex]) {
              // Complete re-render for this item to properly update arrows and polyline
              this.map.removeLayer(this.markers[selectedIndex]);

              const points = item.latlng.map((p: any) => [p.lat, p.lng]);
              const polyline = L.polyline(points, { color: '#ff9800', weight: 5 });
              const groupLayers: any[] = [polyline];

              if (points.length >= 2) {
                for (let i = 0; i < points.length; i++) {
                  let angle = 0;
                  if (i < points.length - 1) {
                    angle = this.getAngle(points[i], points[i + 1]);
                  } else {
                    angle = this.getAngle(points[i - 1], points[i]);
                  }
                  let color = item.selected ? '#ff9800' : '#2196f3';

                  groupLayers.push(L.marker(points[i], { icon: this.getArrowIcon(color, angle), interactive: false }));
                }
              }

              const group = L.featureGroup(groupLayers).addTo(this.map);
              if (item.name) {
                group.bindPopup(`<div style="font-size: 15px; font-weight: 600;">${item.name}</div>`, { closeButton: false });
              }
              this.markers[selectedIndex] = group;

            } else {
              const points = item.latlng.map((p: any) => [p.lat, p.lng]);
              const polyline = L.polyline(points, { color: '#ff9800', weight: 5 });
              const groupLayers: any[] = [polyline];

              if (points.length >= 2) {
                for (let i = 0; i < points.length; i++) {
                  let angle = 0;
                  if (i < points.length - 1) {
                    angle = this.getAngle(points[i], points[i + 1]);
                  } else {
                    angle = this.getAngle(points[i - 1], points[i]);
                  }

                  let color = item.selected ? '#ff9800' : '#2196f3';

                  groupLayers.push(L.marker(points[i], { icon: this.getArrowIcon(color, angle), interactive: false }));
                }
              }

              const group = L.featureGroup(groupLayers).addTo(this.map);

              if (item.name) {
                group.bindPopup(`<div style="font-size: 15px; font-weight: 600;">${item.name}</div>`, { closeButton: false });
              }
              this.markers[selectedIndex] = group;
            }
          } else {
            this.toastr.warning('Please select a multi-location asset to draw a line.');
          }
        } else {
          this.toastr.warning('Please select an asset from the list first.');
        }
      }
    });

    if (this.listingResponse && this.listingResponse.length > 0) {
      const latLngs: any[] = [];
      this.markers = [];

      this.listingResponse.forEach((item: any, index: number) => {
        if (item.multilatlng) {
          if (item.latlng && item.latlng.length > 0) {
            const points = item.latlng.map((p: any) => [p.lat, p.lng]);
            latLngs.push(...points);

            const polylineColor = item.selected ? '#ff9800' : '#2196f3';
            const polyline = L.polyline(points, { color: polylineColor, weight: 5 });
            const groupLayers: any[] = [polyline];

            if (points.length >= 2) {
              for (let i = 0; i < points.length; i++) {
                let angle = 0;
                if (i < points.length - 1) {
                  angle = this.getAngle(points[i], points[i + 1]);
                } else {
                  angle = this.getAngle(points[i - 1], points[i]);
                }

                let color = item.selected ? '#ff9800' : '#2196f3';

                groupLayers.push(L.marker(points[i], { icon: this.getArrowIcon(color, angle), interactive: false }));
              }
            }

            const group = L.featureGroup(groupLayers).addTo(this.map);

            if (item.name) {
              group.bindPopup(`<div style="font-size: 15px; font-weight: 600;">${item.name}</div>`, { closeButton: false });
            }

            this.markers.push(group);
          } else {
            this.markers.push(null);
          }
        } else {
          if (item.lat && item.lng) {
            latLngs.push([item.lat, item.lng]);

            const customHtmlIcon = this.getMarkerIcon(item);

            const marker = L.marker([item.lat, item.lng], {
              icon: customHtmlIcon,
              draggable: !!item.selected
            }).addTo(this.map);

            marker.on('dragend', (event: any) => {
              const position = event.target.getLatLng();
              this.listingResponse[index].lat = position.lat;
              this.listingResponse[index].lng = position.lng;
            });

            if (item.name) {
              marker.bindPopup(`<div style="font-size: 15px; font-weight: 600;">${item.name}</div>`, { closeButton: false });
            }

            this.markers.push(marker);
          } else {
            this.markers.push(null);
          }
        }
      });

      if (latLngs.length > 0) {
        const bounds = L.latLngBounds(latLngs);
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
        this.map.invalidateSize();
      }

      this.map.on('click', (e: any) => {
        const selectedIndex = this.listingResponse.findIndex((item: any) => item.selected);
        if (selectedIndex !== -1) {
          const item = this.listingResponse[selectedIndex];
          if (item.multilatlng) {
            // Use draw control for drawing polylines
          } else {
            if (!item.lat || !item.lng) {
              item.lat = e.latlng.lat;
              item.lng = e.latlng.lng;

              const customHtmlIcon = this.getMarkerIcon(item);

              const marker = L.marker([item.lat, item.lng], {
                icon: customHtmlIcon,
                draggable: true
              }).addTo(this.map);

              marker.on('dragend', (event: any) => {
                const position = event.target.getLatLng();
                this.listingResponse[selectedIndex].lat = position.lat;
                this.listingResponse[selectedIndex].lng = position.lng;
              });

              if (item.name) {
                marker.bindPopup(`<div style="font-size: 15px; font-weight: 600;">${item.name}</div>`, { closeButton: false });
              }

              this.markers[selectedIndex] = marker;
              this.markers[selectedIndex].setZIndexOffset(1000);
            }
          }
        }
      });
    }
  }
}
